import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import AuthContext from '../../context/AuthContext';

const Profile = () => {
  const { user, setUser } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    jobTitle: '',
    department: '',
    isMultiOrg: false
  });
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [error, setError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        jobTitle: user.jobTitle || '',
        department: user.department || '',
        isMultiOrg: user.isMultiOrg || false
      });
    }
  }, [user]);

  const handleChange = (e) => {
    // Skip changes to isMultiOrg as it's now read-only
    if (e.target.name === 'isMultiOrg') return;
    
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };
  
  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const res = await axios.put('/api/users/profile', formData);
      setUser(res.data);
      setSuccess('Profile updated successfully');
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.msg || 'Error updating profile');
      setLoading(false);
    }
  };
  
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    // Reset status messages
    setPasswordError('');
    setPasswordSuccess('');
    
    // Validate password inputs
    if (!passwordData.currentPassword) {
      setPasswordError('Current password is required');
      return;
    }
    
    if (!passwordData.newPassword) {
      setPasswordError('New password is required');
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    // Minimum password length validation
    if (passwordData.newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return;
    }
    
    setPasswordLoading(true);
    
    try {
      await axios.post('/api/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      
      // Clear password fields on success
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      setPasswordSuccess('Password changed successfully');
      setPasswordLoading(false);
    } catch (err) {
      setPasswordError(err.response?.data?.msg || 'Error changing password');
      setPasswordLoading(false);
    }
  };

  const styles = {
    container: {
      maxWidth: '800px',
      margin: '40px auto',
      padding: '30px',
      backgroundColor: '#FFFAFA',
      borderRadius: '8px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
    },
    divider: {
      borderBottom: '1px solid #D2BA7B',
      margin: '30px 0'
    },
    header: {
      borderBottom: '2px solid #D2BA7B',
      paddingBottom: '15px',
      marginBottom: '25px'
    },
    sectionTitle: {
      color: '#243444',
      fontSize: '20px',
      fontWeight: 'bold',
      marginBottom: '20px'
    },
    title: {
      color: '#243444',
      fontSize: '24px',
      fontWeight: 'bold'
    },
    form: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '20px'
    },
    fullWidth: {
      gridColumn: '1 / span 2'
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column'
    },
    label: {
      marginBottom: '8px',
      color: '#243444',
      fontWeight: 'bold'
    },
    input: {
      padding: '10px',
      borderRadius: '4px',
      border: '1px solid #4C5454',
      fontSize: '16px'
    },
    buttonContainer: {
      gridColumn: '1 / span 2',
      marginTop: '20px',
      display: 'flex',
      justifyContent: 'flex-end'
    },
    button: {
      backgroundColor: '#243444',
      color: '#FFFAFA',
      border: 'none',
      padding: '12px 24px',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: 'bold',
      transition: 'background-color 0.3s ease'
    },
    success: {
      backgroundColor: '#4CAF50',
      color: 'white',
      padding: '10px',
      borderRadius: '4px',
      marginBottom: '20px'
    },
    error: {
      backgroundColor: '#7C1227',
      color: 'white',
      padding: '10px',
      borderRadius: '4px',
      marginBottom: '20px'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>My Profile</h2>
      </div>

      {success && <div style={styles.success}>{success}</div>}
      {error && <div style={styles.error}>{error}</div>}

      <h3 style={styles.sectionTitle}>Personal Information</h3>
      <form onSubmit={handleSubmit}>
        <div style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              style={styles.input}
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              style={styles.input}
              required
              disabled
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Phone</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              style={styles.input}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Job Title</label>
            <input
              type="text"
              name="jobTitle"
              value={formData.jobTitle}
              onChange={handleChange}
              style={styles.input}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Department</label>
            <input
              type="text"
              name="department"
              value={formData.department}
              onChange={handleChange}
              style={styles.input}
            />
          </div>

          <div style={{...styles.formGroup, ...styles.fullWidth, marginTop: '20px'}}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                id="isMultiOrg"
                name="isMultiOrg"
                checked={formData.isMultiOrg}
                disabled={true}
                style={{ marginRight: '10px', width: '20px', height: '20px', cursor: 'not-allowed', opacity: 0.7 }}
              />
              <label htmlFor="isMultiOrg" style={{ fontWeight: 'bold', color: '#243444' }}>
                I work with multiple organizations
              </label>
            </div>
            <p style={{ margin: '10px 0 0 30px', color: '#4C5454', fontSize: '14px' }}>
              Enable this option if you need to manage multiple client organizations. When enabled, you'll be able to add and switch between different organizations in the Organization Settings page.
              <br />
              <span style={{ fontStyle: 'italic', color: '#7C1227' }}>This setting can only be changed by an administrator.</span>
            </p>
          </div>

          <div style={styles.buttonContainer}>
            <button 
              type="submit" 
              style={styles.button}
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </form>
      
      <div style={styles.divider}></div>
      
      <h3 style={styles.sectionTitle}>Change Password</h3>
      {passwordSuccess && <div style={styles.success}>{passwordSuccess}</div>}
      {passwordError && <div style={styles.error}>{passwordError}</div>}
      
      <form onSubmit={handlePasswordSubmit}>
        <div style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Current Password</label>
            <input
              type="password"
              name="currentPassword"
              value={passwordData.currentPassword}
              onChange={handlePasswordChange}
              style={styles.input}
              required
            />
          </div>
          
          <div style={styles.formGroup}>
            <label style={styles.label}>New Password</label>
            <input
              type="password"
              name="newPassword"
              value={passwordData.newPassword}
              onChange={handlePasswordChange}
              style={styles.input}
              required
              minLength="8"
            />
          </div>
          
          <div style={styles.formGroup}>
            <label style={styles.label}>Confirm New Password</label>
            <input
              type="password"
              name="confirmPassword"
              value={passwordData.confirmPassword}
              onChange={handlePasswordChange}
              style={styles.input}
              required
            />
          </div>
          
          <div style={{...styles.formGroup, gridColumn: '2'}}>
            <p style={{fontSize: '14px', color: '#4C5454', marginTop: '10px'}}>
              Password must be at least 8 characters long.
            </p>
          </div>
          
          <div style={styles.buttonContainer}>
            <button 
              type="submit" 
              style={{...styles.button, backgroundColor: '#4C5454'}}
              disabled={passwordLoading}
            >
              {passwordLoading ? 'Changing Password...' : 'Change Password'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Profile;
